import React from "react";
import { createTheme } from "@material-ui/core/styles";
import { BrandsEnum } from "../enums";
import * as ToggleButtons from "./common/ToggleButtonCommonBase";
import * as Helpers from "./common/Helpers";
import * as DatePicker from "./common/DateTimePickerCommonBase";
import { PositionProperty, TextTransformProperty } from "csstype";

// create default theme to get breakpoints
const theme = createTheme();
const colorCard = {};

const variables = {
  common: {
    borderRadius: 4,
  },
  colors: {
    primary: {
      main: "#FF0036",
      dark: "#000000",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#FFFFFF",
      dark: "#000000",
      contrastText: "#FFFFFF",
    },
    error: {
      main: "#C82E2D",
    },
    info: {
      main: "#2D71D7",
      light: "#E7F2FA",
    },
    text: {
      primary: "#0F0F0F",
      secondary: "#0F0F0F",
      hint: "#808890",
    },

    background: "#FAFAF9",
    backgroundHeader: "#FFFFFF",
    border: "#6F6F6F",
    hover: "#DEDAD4",
    placeholder: "#949494",
    transparent: "#00000000",
    divider: "#FFF",
    selectedBackground: "#DEDAD4",
    hoverBorder: "rgba(15,15,15,0.6)",
    hoverButton: "#b0042c",
  },
};
const svgIcons = {
  circleRemove: {
    d: "path('M7 11v2h10v-2H7zm5-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z')",
  },
};

const defaultPathParams: React.SVGProps<SVGPathElement> = {
  stroke: "currentColor",
  strokeWidth: "1.5",
  fill: "none",
};

function getFontFamily(mainFont: string): string {
  return [mainFont ?? "", "Roboto", '"Helvetica Neue"', "Arial", "sans-serif"].join(",");
}

const props = Helpers.PrepareProperties(variables);

// More properties here: https://material-ui.com/customization/default-theme/
export const seat = {
  themeName: "Seat",
  brand: BrandsEnum.Seat,
  colorCard: colorCard,
  compositionBoxWidth: 816,
  containerMaxWidth: 412,
  pageMaxWidth: 1208,
  centred: "0 auto",
  border: "1px solid ",
  shape: {
    borderRadius: variables.common.borderRadius,
  },
  palette: {
    primary: variables.colors.primary,
    secondary: variables.colors.secondary,
    error: variables.colors.error,
    text: variables.colors.text,
    backgroundSecondary: {
      default: variables.colors.backgroundHeader,
      paper: variables.colors.selectedBackground,
    },
    background: {
      default: variables.colors.background,
      paper: "#FFF",
    },
    divider: variables.colors.divider,
    grey: {
      400: variables.colors.border,
    },
    disabledColor: variables.colors.placeholder,
    info: variables.colors.info,
  },
  typography: {
    fontFamily: getFontFamily("SEAT Bcn"),
    color: variables.colors.text.primary,
    h1: {
      fontSize: "2rem",
      lineHeight: "2.25rem",
      color: variables.colors.text.secondary,
      fontWeight: 900,
      [theme.breakpoints.up("md")]: {
        fontSize: "2.5rem",
        lineHeight: "3rem",
      },
      textAlign: "center" as "center",
    },
    h2: {
      fontSize: "2rem",
      lineHeight: "2.25rem",
      color: variables.colors.text.secondary,
      fontWeight: 900,
      textAlign: "center" as "center",
      [theme.breakpoints.up("md")]: {
        fontSize: "2rem",
        lineHeight: "2.5rem",
      },
      paddingBottom: theme.spacing(4),
    },
    h3: {
      fontSize: "1.25rem",
      lineHeight: "2.25rem",
      color: variables.colors.primary.main,
      fontWeight: 900,
      textDecoration: "underline",
    },
    h4: {
      color: variables.colors.text.primary,
      fontWeight: 900,
      fontSize: "1.25rem",
      lineHeight: "1.75rem",
    },
    h5: {
      fontSize: "1.25rem",
      color: variables.colors.text.primary,
      fontWeight: 900,
    },
    h6: {
      color: variables.colors.secondary.main,
      fontSize: "1rem",
      lineHeight: "1.75rem",
    },
    // helper text
    caption: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
    },
    button: {
      fontSize: "1rem",
      fontWeight: "normal" as "normal",
      lineHeight: "1.5rem",
      minHeight: 48,
    },
    subtitle2: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
      color: variables.colors.text.primary,
      fontWeight: "normal" as "normal",
    },
    body1: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
      color: variables.colors.text.primary,
    },
    body2: {
      fontSize: "1.2rem",
      lineHeight: "1.5rem",
      color: variables.colors.primary.main,
    },
  },
  radioButton: {
    borderRadius: variables.common.borderRadius,
    border: `1px solid ${variables.colors.border}`,
    hoverBackgroundColor: variables.colors.hover,
    activeBorder: `2px solid ${variables.colors.primary.main}`,
    activeBackgroundColor: "#FFFFFF",
    activeFontWeight: 500,
  },
  addOnCard: {
    cardActions: {
      "& .MuiFormControlLabel-label": {
        fontWeight: 500,
      },
    },
    addText: {
      color: "#ff0036",
    },
    addTextSelected: {
      color: "#ff0036",
    },
    component: {
      width: 380,
      display: "inline-block",
      marginBottom: "8px",
    },
    card: {
      border: `1px solid ${variables.colors.border}`,
      borderRadius: 4,
      padding: "25px 34px",
    },
    cardSelected: {
      border: `2px solid ${variables.colors.primary.main}`,
      padding: "25px 34px",
      backgroundColor: "#FFFFFF",
      addText: {
        color: "#ff0036",
      },
    },
    header: {
      textColor: variables.colors.text.secondary,
      fontWeight: 900,
    },
    price: {
      color: variables.colors.text.secondary,
    },
  },
  checkoutLinks: {
    color: variables.colors.primary.main,
  },
  popup: {
    root: {
      fontFamily: getFontFamily("SEAT Bcn"),
      backgroundColor: "#FFF",
      "& p": {
        color: "#0F0F0F",
      },
    },
    h4: {
      color: "#0F0F0F",
      fontWeight: 700,
    },
  },
  uspContainerBlockWrapper: {
    backgroundColor: "#33302E",
  },
  uspBlock: {
    header: {
      fontWeight: 500,
      color: "#FFFFFF",
      fontSize: "1.25rem",
      lineHeight: "1.75rem",
      fontFamily: getFontFamily("SEAT Bcn"),
      [theme.breakpoints.up("md")]: {
        fontSize: "1.5rem",
        lineHeight: "2rem",
      },
    },
    text: {
      fontWeight: 400,
      color: "#FFFFFF",
      fontSize: "1rem",
      lineHeight: "1.5rem",
    },
  },
  overrides: {
    // Style sheet name. Look for specific component CSS API: https://material-ui.com/api/button/#css
    MuiTypography: {
      root: {
        textTransform: "none" as TextTransformProperty,
      },
    },
    MuiButton: {
      // Name of the rule
      root: {
        // Some CSS
        textTransform: "none" as TextTransformProperty,
      },
      contained: {
        boxShadow: props.buttons.boxShadow,
        borderRadius: 4,
        "&:hover": {
          boxShadow: props.buttons.boxShadow,
        },
        "&:active": {
          boxShadow: props.buttons.boxShadow,
        },
        "&$focusVisible": {
          boxShadow: props.buttons.boxShadow,
        },
      },
      containedPrimary: {
        backgroundColor: variables.colors.primary.main,
        fontWeight: 400,
        color: variables.colors.primary.contrastText,
        textDecoration: "none",
        "&:hover": {
          border: "1px solid " + variables.colors.hoverButton,
          color: variables.colors.secondary.contrastText,
          backgroundColor: variables.colors.hoverButton,
          "& span": {
            textDecoration: "none",
          },
        },
      },
      containedSecondary: {
        "&:hover": {
          border: "2px solid " + variables.colors.hoverBorder,
          color: variables.colors.primary.dark,
          backgroundColor: variables.colors.transparent,
          "& span": {
            textDecoration: "none",
          },
        },
        "&:active": {
          border: "2px solid " + variables.colors.hoverBorder,
          color: variables.colors.primary.dark,
          backgroundColor: variables.colors.transparent,
          "& span": {
            textDecoration: "none",
          },
        },
        color: variables.colors.primary.main,
        backgroundColor: variables.colors.primary.dark,
        border: "2px solid " + variables.colors.primary.dark,
        // this is just to make link styling same as button if link has button classes
        // needed to override link styling by external CSS by making selector more specific
        "a&&": {
          "&:hover": {
            border: "2px solid " + variables.colors.hoverBorder,
            color: variables.colors.primary.dark,
            backgroundColor: variables.colors.transparent,
            "& span": {
              textDecoration: "none",
            },
          },
          "&:active": {
            border: "2px solid " + variables.colors.hoverBorder,
            color: variables.colors.primary.dark,
            backgroundColor: variables.colors.transparent,
            "& span": {
              textDecoration: "none",
            },
          },
          color: variables.colors.primary.dark,
          backgroundColor: variables.colors.transparent,
          border: "2px solid " + variables.colors.primary.dark,
        },
      },
      outlinedSecondary: {
        "&:hover": {
          border: "2px solid " + variables.colors.primary.dark,
          color: variables.colors.primary.contrastText,
          backgroundColor: variables.colors.primary.dark,
        },
        "&:active": {
          border: "2px solid " + variables.colors.primary.dark,
          color: variables.colors.primary.contrastText,
          backgroundColor: variables.colors.primary.dark,
        },
        color: variables.colors.primary.dark,
        border: "2px solid " + variables.colors.primary.dark,
      },
      text: {
        "&:hover": {
          color: variables.colors.secondary.dark,
        },
        "&:active": {
          color: variables.colors.text.hint,
        },
        color: variables.colors.text.primary,
      },
      textPrimary: {
        color: variables.colors.primary.dark,
        "&:hover": {
          color: variables.colors.hoverButton,
          backgroundColor: variables.colors.transparent,
        },
        "&:active": {
          backgroundColor: variables.colors.transparent,
        },
      },
      /* Styles applied to the icon element if supplied and `size="medium"`. */
      iconSizeMedium: {
        "& > *:first-child": {
          fontSize: 20,
        },
      },
    },
    MuiRadio: {
      root: {
        color: variables.colors.primary.main,
      },
      colorSecondary: {
        "&.Mui-checked": {
          color: variables.colors.primary.main,
        },
      },
    },
    MuiIconButton: {
      root: {
        color: variables.colors.primary.main,
        "&:hover": {
          color: variables.colors.primary.dark,
        },
      },
      sizeSmall: {
        padding: 0,
      },
    },
    MuiFormLabel: {
      root: {
        color: variables.colors.text.secondary,
        "&$focused": {
          color: variables.colors.border,
        },
        fontWeight: 400,
      },
    },
    MuiInputLabel: {
      formControl: {
        color: variables.colors.text.secondary,
        position: "relative" as PositionProperty,
        transform: "none",
        fontWeight: 400,
        lineHeight: "1.75rem", // safe to use InputLabel + <SvgIcon size='small' />
      },
    },
    MuiOutlinedInput: {
      root: {
        borderBottom: "1px solid " + variables.colors.border,
        borderRadius: 0,
        backgroundColor: variables.colors.background,
        "&:hover": {
          backgroundColor: variables.colors.hover,
          "& $notchedOutline": {
            borderColor: variables.colors.border,
          },
          "&$focused $notchedOutline": {
            borderColor: variables.colors.primary.main,
          },
          "&$error $notchedOutline": {
            borderColor: variables.colors.error.main,
          },
        },
        "&$focused $notchedOutline": {
          borderColor: variables.colors.primary.main,
          borderWidth: 0,
        },
      },
      notchedOutline: {
        borderColor: variables.colors.border,
        borderWidth: 0,
      },
      input: {
        "&::-webkit-input-placeholder": props.inputs.placeholder,
        "&::-moz-placeholder": props.inputs.placeholder, // Firefox 19+
        "&:-ms-input-placeholder": props.inputs.placeholder, // IE 11
        "&::-ms-input-placeholder": props.inputs.placeholder, // Edge
      },
    },
    MuiFormHelperText: {
      root: {
        color: variables.colors.text.hint,
      },
      contained: {
        marginLeft: 0,
      },
    },
    MuiListItem: {
      ...props.listItems,
      button: {
        backgroundColor: variables.colors.background,
        "&:hover": {
          backgroundColor: variables.colors.hover,
        },
        "&$selected": {
          backgroundColor: variables.colors.background,
          "&:hover": {
            backgroundColor: variables.colors.hover,
          },
        },
      },
    },
    MuiMenuItem: {
      ...props.listItems,
    },
    MuiSelect: {
      select: {
        "&:focus": {
          backgroundColor: variables.colors.transparent,
        },
        // Setting icon with IconComponent property, looses all select styling, so we change SVG path directly to one we need
        "&~ svg": {
          "&> path": {
            d: "path('M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z')",
          },
        },
      },
      icon: {
        fill: variables.colors.primary.dark,
      },
    },
    MuiList: {
      padding: {
        paddingTop: 0,
        paddingBottom: 0,
      },
    },
    MuiSvgIcon: {
      fontSizeInherit: {
        fontSize: "56px",
      },
    },
    MuiLink: {
      underlineHover: {
        "a&": {
          // to increase specificy
          color: variables.colors.text.secondary,
          textDecoration: "underline",
          "&:hover": {
            color: "#6F6F6F",
            textDecoration: "underline",
          },
          "&:active": {
            color: variables.colors.primary.main,
          },
        },
      },
    },
    MuiFormControlLabel: {
      label: {
        color: variables.colors.text.primary,
        fontWeight: 400,
      },
    },
    MuiToggleButtonGroup: {
      root: {
        ...ToggleButtons.CommonBase.MuiToggleButtonGroup.root,
        borderRadius: variables.common.borderRadius,
      },
      groupedHorizontal: {
        ...ToggleButtons.CommonBase.MuiToggleButtonGroup.groupedHorizontal,
        ...ToggleButtons.SetMuiToggleButtonGroupBorders({
          ...theme,
          shape: {
            borderRadius: variables.common.borderRadius,
          },
        }),
      },
    },
    MuiToggleButton: {
      root: {
        ...ToggleButtons.CommonBase.MuiToggleButton.root,
        color: variables.colors.placeholder,
        border: "1px solid " + variables.colors.border,
        fontWeight: 400,
        "&$selected": {
          color: "#0F0F0F",
          fontWeight: 500,
          backgroundColor: "#FFFFFF",
          borderColor: variables.colors.primary.main,
          borderWidth: 2,
          "&:hover": {
            backgroundColor: variables.colors.hover,
          },
        },
        "&:hover": {
          backgroundColor: variables.colors.hover,
        },
      },
    },
    MuiPickersBasePicker: {
      container: {
        ...DatePicker.CommonBase.MuiPickersBasePicker.container,
      },
    },
    MuiCard: {
      root: {
        width: 385,
      },
    },
  },
  footerBar: {
    backgroundColor: "#0F0F0F",
    textColor: variables.colors.secondary.contrastText,
  },
  priceText: {
    totalPriceColor: variables.colors.text.primary,
    fontWeight: 700,
  },
  header: {
    "& *": {
      color: variables.colors.primary.dark,
    },
  },
  outlinedButton: {
    "& span": {
      color: variables.colors.text.secondary,
      width: "100%",
      height: "100%",
      minHeight: "56px",
    },
    "& span:hover": {
      color: variables.colors.primary.dark,
    },
    padding: "0",
    minHeight: "56px",
    display: "flex",
  },
  icons: {
    dateTimeIcon: (
      <path
        {...defaultPathParams}
        strokeLinecap='square'
        strokeMiterlimit='10'
        d='m 2.4615,5.7769 h 20.577 V 21.5526 H 2.4615 Z m 20.577,3.4295 H 2.4615 M 17.5513,2.3474 V 5.7769 M 7.9487,2.3474 v 3.4295'
      />
    ),
    infoIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M12.0015 2C6.48567 2 2 6.48567 2 12.0015C2 17.5174 6.48567 22.0031 12.0015 22.0031C17.5174 22.0031 22.0031 17.5174 22.0031 12.0015C22.0031 6.48567 17.5143 2 12.0015 2ZM12.0015 21.0802C6.99463 21.0802 2.91982 17.0054 2.91982 11.9985C2.91982 6.99157 6.99463 2.91982 12.0015 2.91982C17.0084 2.91982 21.0832 6.99463 21.0832 12.0015C21.0832 17.0084 17.0084 21.0832 12.0015 21.0832V21.0802Z'
          fill='#0F0F0F'
        />
        <path
          d='M12.0014 9.0029C12.8753 9.0029 13.5896 8.29157 13.5896 7.41774C13.5896 6.54391 12.8783 5.83258 12.0014 5.83258C11.1245 5.83258 10.4163 6.54391 10.4163 7.41774C10.4163 8.29157 11.1276 9.0029 12.0014 9.0029ZM12.0014 6.74934C12.3693 6.74934 12.6698 7.04981 12.6698 7.41774C12.6698 7.78567 12.3693 8.08614 12.0014 8.08614C11.6335 8.08614 11.3361 7.78567 11.3361 7.41774C11.3361 7.04981 11.6366 6.74934 12.0014 6.74934Z'
          fill='#0F0F0F'
        />
        <path
          d='M11.6151 9.70197H10.6187V16.6681C10.6187 17.64 11.4097 18.4311 12.3816 18.4311H13.3781V17.5113H12.4583H12.3816C11.9156 17.5113 11.5385 17.1341 11.5385 16.6681V10.6218H11.6151C12.0812 10.6218 12.4583 10.9989 12.4583 11.465V16.1131H13.3781V11.465C13.3781 10.493 12.5871 9.70197 11.6151 9.70197Z'
          fill='#0F0F0F'
        />
      </svg>
    ),
    helpIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M12.0015 2C6.48567 2 2 6.48567 2 12.0015C2 17.5174 6.48567 22.0031 12.0015 22.0031C17.5174 22.0031 22.0031 17.5174 22.0031 12.0015C22.0031 6.48567 17.5143 2 12.0015 2ZM12.0015 21.0802C6.99463 21.0802 2.91982 17.0054 2.91982 11.9985C2.91982 6.99157 6.99463 2.91982 12.0015 2.91982C17.0084 2.91982 21.0832 6.99463 21.0832 12.0015C21.0832 17.0084 17.0084 21.0832 12.0015 21.0832V21.0802Z'
          fill='#0F0F0F'
        />
        <path
          d='M12.0014 9.0029C12.8753 9.0029 13.5896 8.29157 13.5896 7.41774C13.5896 6.54391 12.8783 5.83258 12.0014 5.83258C11.1245 5.83258 10.4163 6.54391 10.4163 7.41774C10.4163 8.29157 11.1276 9.0029 12.0014 9.0029ZM12.0014 6.74934C12.3693 6.74934 12.6698 7.04981 12.6698 7.41774C12.6698 7.78567 12.3693 8.08614 12.0014 8.08614C11.6335 8.08614 11.3361 7.78567 11.3361 7.41774C11.3361 7.04981 11.6366 6.74934 12.0014 6.74934Z'
          fill='#0F0F0F'
        />
        <path
          d='M11.6151 9.70197H10.6187V16.6681C10.6187 17.64 11.4097 18.4311 12.3816 18.4311H13.3781V17.5113H12.4583H12.3816C11.9156 17.5113 11.5385 17.1341 11.5385 16.6681V10.6218H11.6151C12.0812 10.6218 12.4583 10.9989 12.4583 11.465V16.1131H13.3781V11.465C13.3781 10.493 12.5871 9.70197 11.6151 9.70197Z'
          fill='#0F0F0F'
        />
      </svg>
    ),
    closeIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M4 19.0797L11.0945 11.9852L4 4.89013L4.89073 4L11.9852 11.0945L19.0797 4L19.9704 4.86054L12.8759 11.9852L20 19.1093L19.1099 20L11.9852 12.8753L4.89073 19.9704L4 19.0797Z'
          fill='#1A1A1A'
        />
      </svg>
    ),
    checkmarkIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M11.9983 22C17.511 22 21.9967 17.5143 21.9967 12.0017C21.9967 9.71366 21.244 7.55946 19.819 5.77321L19.3373 6.81017C20.4178 8.33216 20.9931 10.1151 20.9931 12.0017C20.9931 16.9624 16.959 20.9965 11.9983 20.9965C7.03763 20.9965 3.00351 16.959 3.00351 11.9983C3.00351 7.03763 7.03763 3.00351 11.9983 3.00351C13.6575 3.00351 15.2631 3.45509 16.668 4.30808L11.9114 14.487L8.99783 10.299L8.17495 10.871L12.0853 16.4874L17.9458 3.94681L17.5912 3.70597C15.9354 2.59207 14.002 2 11.9983 2C6.4857 2 2 6.4857 2 11.9983C2 17.511 6.4857 21.9967 11.9983 21.9967V22Z'
          fill='#0F0F0F'
        />
      </svg>
    ),
    crossIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <g opacity='0.4'>
          <path d='M12.246 11.7107L11.6229 12.3338L16.0115 16.7224L16.6346 16.0993L12.246 11.7107Z' fill='#0F0F0F' />
          <path d='M7.85543 7.3212L7.2323 7.94434L11.6209 12.333L12.2441 11.7098L7.85543 7.3212Z' fill='#0F0F0F' />
          <path d='M16.0058 7.32273L11.6172 11.7114L12.2403 12.3345L16.629 7.94587L16.0058 7.32273Z' fill='#0F0F0F' />
          <path d='M11.6164 11.7091L7.22778 16.0977L7.85092 16.7208L12.2395 12.3322L11.6164 11.7091Z' fill='#0F0F0F' />
          <path
            d='M12 22C6.48594 22 2 17.5141 2 12C2 6.48594 6.48594 2 12 2C17.5141 2 22 6.48594 22 12C22 12.5623 21.9538 13.1246 21.8615 13.6744L20.9929 13.5275C21.0768 13.0239 21.1188 12.512 21.1188 12C21.1188 6.97272 17.0273 2.88124 12 2.88124C6.97272 2.88124 2.88124 6.97272 2.88124 12C2.88124 17.0273 6.97272 21.1188 12 21.1188C15.483 21.1188 18.6093 19.18 20.1662 16.0663L20.9551 16.4608C19.2514 19.8808 15.8187 22.0042 12 22.0042V22Z'
            fill='#0F0F0F'
          />
        </g>
      </svg>
    ),
    checkmarkIconCSS: encodeURIComponent(
      `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M11.9983 22C17.511 22 21.9967 17.5143 21.9967 12.0017C21.9967 9.71366 21.244 7.55946 19.819 5.77321L19.3373 6.81017C20.4178 8.33216 20.9931 10.1151 20.9931 12.0017C20.9931 16.9624 16.959 20.9965 11.9983 20.9965C7.03763 20.9965 3.00351 16.959 3.00351 11.9983C3.00351 7.03763 7.03763 3.00351 11.9983 3.00351C13.6575 3.00351 15.2631 3.45509 16.668 4.30808L11.9114 14.487L8.99783 10.299L8.17495 10.871L12.0853 16.4874L17.9458 3.94681L17.5912 3.70597C15.9354 2.59207 14.002 2 11.9983 2C6.4857 2 2 6.4857 2 11.9983C2 17.511 6.4857 21.9967 11.9983 21.9967V22Z" fill="#0F0F0F" />
      </svg>`,
    ),
    backIcon: <path {...defaultPathParams} stroke='#000' strokeLinecap='square' strokeMiterlimit='10' d='m 15,6 -6,6 6,6' />,
    checkmarkSelectedOptionIcon: (
      <svg>
        <path d='M-1.2071-.5H48.5v49.7071z' fill='#ff0036' stroke='#ff0036' transform='matrix(1.24418 0 0 1.2493 3.00373 1.01704)' />
        <path
          d='M11.9983 22C17.511 22 21.9967 17.5143 21.9967 12.0017C21.9967 9.71366 21.244 7.55946 19.819 5.77321L19.3373 6.81017C20.4178 8.33216 20.9931 10.1151 20.9931 12.0017C20.9931 16.9624 16.959 20.9965 11.9983 20.9965C7.03763 20.9965 3.00351 16.959 3.00351 11.9983C3.00351 7.03763 7.03763 3.00351 11.9983 3.00351C13.6575 3.00351 15.2631 3.45509 16.668 4.30808L11.9114 14.487L8.99783 10.299L8.17495 10.871L12.0853 16.4874L17.9458 3.94681L17.5912 3.70597C15.9354 2.59207 14.002 2 11.9983 2C6.4857 2 2 6.4857 2 11.9983C2 17.511 6.4857 21.9967 11.9983 21.9967V22Z'
          fill='#FFF'
          transform='translate(11 1) scale(0.5)'
        />
      </svg>
    ),
    addIcon: <path {...defaultPathParams} strokeLinecap='square' strokeMiterlimit='10' d='M 20,12 H 4 m 8,-8 v 16' />,
    addIconDisabled: (
      <path stroke='#6F6F6F' strokeWidth='1.5' fill='none' strokeLinecap='square' strokeMiterlimit='10' d='M 20,12 H 4 m 8,-8 v 16' />
    ),
    statusIndicatorIcon: <circle cx='6' cy='6' r='5' fill={variables.colors.info.main} stroke='white' strokeWidth='2' />,
  },
  messageBox: {
    backgroundColor: "#E3E9F5",
    borderColor: variables.colors.transparent,
    dividerColor: variables.colors.border,
    textAlign: "left",
  },
  coverageSelect: {
    root: {
      CoverageHeaderName: {
        color: variables.colors.text.secondary,
        fontWeight: 700,
      },
      CoverageItemText: {
        color: variables.colors.text.secondary,
      },
    },
    selected: {
      CoverageHeaderName: {
        fontFamily: getFontFamily("SEAT Bcn"),
      },
      CoverageHeaderPrice: {
        fontFamily: getFontFamily("SEAT Bcn"),
      },
      textColor: variables.colors.text.secondary,
      paddingBottom: 0,
      borderWidth: 2,
    },
    descriptionCard: {
      backgroundColor: variables.colors.selectedBackground,
      borderColor: variables.colors.selectedBackground,
      textColor: variables.colors.text.primary,
    },
  },
  CheckoutPage: {
    priceText: {
      color: variables.colors.text.secondary,
    },
    bottomTotalPriceText: {
      color: "#0F0F0F",
    },
  },
  ConfirmationPage: {
    ConfirmationButtonOutlined: {
      color: variables.colors.primary.dark,
    },
  },
  confirmationPageHeader: {
    color: variables.colors.text.secondary,
  },
  skeletonHeaderText: {
    background: theme.palette.secondary.contrastText,
  },
  paymentOption: {
    selectButton: {
      "& span": {
        color: variables.colors.primary.main,
      },
      "& .MuiFormControlLabel-label": {
        fontWeight: 500,
      },
    },
    label: {
      lineHeight: "1.75rem",
      color: variables.colors.text.secondary,
    },
    color: variables.colors.text.secondary,
    selectedPayment: {
      borderWidth: 2,
    },
  },
  payment: {
    backgroundColor: "#FFFFFF",
  },
  statusIndicator: {
    borderRadius: 2,
  },
  stickyPriceButton: {
    backgroundColor: variables.colors.primary.main,
    hoverColor: variables.colors.primary.dark,
    borderColor: variables.colors.primary.main,
    textColor: variables.colors.primary.contrastText,
  },
};

export default createTheme(seat);
